import { z } from 'zod';
import { Product, ProductAttribute } from '@/services/productService';
import { Category as ProductCategory } from '@/types/categories';
import { logWarn } from '@/utils/errorHandler';

/**
 * Validates if a string is a valid GTIN (EAN-8, EAN-13, UPC-A, GTIN-14)
 * @param code - The code to validate
 * @returns Whether the code is a valid GTIN
 */
export const isValidGTIN = (code: string): boolean => {
  // Remove any spaces or dashes
  code = code.replace(/[\s-]/g, '');

  // Check if the code contains only digits
  if (!/^\d+$/.test(code)) return false;

  // Check for valid length
  if (![8, 12, 13, 14].includes(code.length)) return false;

  // Checksum validation (Luhn algorithm for GTIN/EAN/UPC)
  let sum = 0;
  const parity = code.length % 2;

  for (let i = 0; i < code.length - 1; i++) {
    let digit = parseInt(code[i], 10);
    if (i % 2 === parity) {
      digit *= 3;
    }
    sum += digit;
  }

  const checkDigit = (10 - (sum % 10)) % 10;
  return checkDigit === parseInt(code[code.length - 1], 10);
};

/**
 * Validates if a string is a reasonable barcode/GTIN format for PIM usage
 * This is more lenient than strict GTIN validation to accommodate real-world usage
 * @param code - The code to validate
 * @returns Whether the code is a reasonable barcode format
 */
export const isValidBarcodeFormat = (code: string): boolean => {
  // Remove any spaces or dashes
  const cleanCode = code.replace(/[\s-]/g, '');

  // Check if the code contains only digits
  if (!/^\d+$/.test(cleanCode)) return false;

  // Allow reasonable lengths (6-18 digits) to accommodate various barcode formats
  // This includes: EAN-8, UPC-A, EAN-13, GTIN-14, ISBN, and other internal codes
  if (cleanCode.length < 6 || cleanCode.length > 18) return false;

  return true;
};

/**
 * Validates if a string is a strict GTIN with proper checksum
 * @param code - The code to validate
 * @returns Whether the code is a valid GTIN with proper checksum
 */
export const isStrictGTIN = (code: string): boolean => {
  return isValidGTIN(code);
};

/**
 * SKU validation with enhanced rules
 */
const skuSchema = z
  .string()
  .min(1, 'SKU is required')
  .min(3, 'SKU must be at least 3 characters')
  .max(50, 'SKU must not exceed 50 characters')
  .regex(/^[A-Za-z0-9_-]+$/, 'SKU can only contain letters, numbers, hyphens, and underscores')
  .refine(val => !val.startsWith('-') && !val.endsWith('-'), {
    message: 'SKU cannot start or end with a hyphen',
  });

/**
 * Name validation with enhanced rules
 */
const nameSchema = z
  .string()
  .min(1, 'Product name is required')
  .min(2, 'Product name must be at least 2 characters')
  .max(200, 'Product name must not exceed 200 characters')
  .refine(val => val.trim().length > 0, {
    message: 'Product name cannot be just whitespace',
  });

/**
 * Price validation
 */
const priceSchema = z.union([
  z.coerce.number({ required_error: 'Price is required' }).min(0.01, 'Price must be at least 0.01'),
  z
    .string()
    .transform(val => parseFloat(val))
    .pipe(z.number().min(0.01)),
]);

/**
 * GTIN/Barcode validation - More lenient for PIM usage
 * Note: Validation messages are handled at the component level for translation
 */
const gtinSchema = z
  .string()
  .refine(val => val === '' || isValidBarcodeFormat(val))
  .optional();

/**
 * Category validation
 */
const categorySchema = z.union([
  z.string(),
  z.number(),
  z.object({
    id: z.union([z.string(), z.number()]),
    name: z.string(),
  }),
  z.array(
    z.object({
      id: z.union([z.string(), z.number()]),
      name: z.string(),
    })
  ),
]);

/**
 * Family validation schema
 */
const familySchema = z
  .union([
    z.string().transform(val => (val === 'none' || val === '' ? null : Number(val))),
    z.number(),
    z.null(),
  ])
  .optional();

/**
 * Product edit validation schema
 */
export const productEditSchema = z.object({
  sku: skuSchema,
  name: nameSchema,
  description: z.string().max(2000, 'Description must not exceed 2000 characters').optional(),
  brand: z.string().max(100, 'Brand must not exceed 100 characters').optional(),
  barcode: gtinSchema,
  price: priceSchema,
  is_active: z.boolean().default(true),
  category: categorySchema.optional(),
  family: familySchema,
  attributes: z.record(z.string()).optional(),
});

/**
 * Dynamically creates a product validation schema based on context and enhanced rules
 * @param context - Validation context (create, edit, etc.)
 * @param enhanced - Whether to use enhanced validation rules
 * @returns Promise resolving to a Zod schema
 */
export async function createDynamicProductSchema(
  context: string = 'create',
  enhanced?: boolean
): Promise<z.ZodObject<z.ZodRawShape>> {
  try {
    // Dynamically import to avoid circular dependencies
    const { createProductValidationSchema } = await import('@/lib/validationSchema');
    return await createProductValidationSchema(context, enhanced);
  } catch (error) {
    logWarn('Failed to load unified validation schema, falling back to legacy schema', {
      component: 'product.ts',
      action: 'createDynamicProductSchema',
      metadata: { context, enhanced, error },
    });
    // Fallback to legacy schema for backward compatibility
    return productEditSchema;
  }
}

/**
 * Creates a validation schema for a specific field using the unified system
 * @param fieldName - Name of the field (e.g., 'sku', 'name', 'description')
 * @param context - Validation context
 * @param enhanced - Whether to use enhanced validation
 * @returns Promise resolving to a Zod schema for the field
 */
export async function createFieldValidationSchema(
  fieldName: string,
  context: string = 'create',
  enhanced?: boolean
): Promise<z.ZodType<any, z.ZodTypeDef, any>> {
  try {
    const { createZodSchemaForField } = await import('@/lib/validationSchema');
    const schema = await createZodSchemaForField(fieldName, context, enhanced);
    // Ensure we never return null
    if (!schema) {
      throw new Error('Schema creation returned null');
    }
    return schema;
  } catch (error) {
    logWarn(`Failed to load validation schema for field ${fieldName}, using fallback`, {
      component: 'product.ts',
      action: 'createFieldValidationSchema',
      metadata: { fieldName, context, enhanced, error },
    });

    // Fallback to legacy validation based on field
    switch (fieldName) {
      case 'sku':
        return z.string().min(1, 'SKU is required');
      case 'name':
        return z.string().min(1, 'Name is required');
      case 'description':
        return z.string().optional();
      case 'brand':
        return z.string().optional();
      case 'barcode':
        return z
          .string()
          .refine(val => val === '' || isValidBarcodeFormat(val))
          .optional();
      case 'price':
        return z.coerce
          .number({ required_error: 'Price is required' })
          .min(0.01, 'Price must be at least 0.01');
      case 'is_active':
        return z.boolean().default(true);
      default:
        return z.string().optional();
    }
  }
}

/**
 * Validates a single field value using the unified validation system
 * @param fieldName - Name of the field
 * @param value - Value to validate
 * @param context - Validation context
 * @param enhanced - Whether to use enhanced validation
 * @returns Promise resolving to validation result
 */
export async function validateField(
  fieldName: string,
  value: unknown,
  context: string = 'create',
  enhanced?: boolean
): Promise<{ isValid: boolean; errors: string[] }> {
  try {
    const fieldSchema = await createFieldValidationSchema(fieldName, context, enhanced);
    const result = fieldSchema.safeParse(value);

    if (result.success) {
      return { isValid: true, errors: [] };
    } else {
      return {
        isValid: false,
        errors: result.error.issues.map((issue: z.ZodIssue) => issue.message),
      };
    }
  } catch (error) {
    logWarn(`Field validation failed for ${fieldName}`, {
      component: 'product.ts',
      action: 'validateField',
      metadata: { fieldName, context, enhanced, error },
    });
    return { isValid: false, errors: ['Validation error occurred'] };
  }
}

/**
 * Checks SKU uniqueness in real-time
 * @param sku - SKU value to check
 * @param organizationId - Organization ID
 * @returns Promise resolving to boolean indicating if SKU is unique
 */
export async function checkSKUUniqueness(sku: string, organizationId: string): Promise<boolean> {
  try {
    const { validateSKUUniqueness } = await import('@/lib/validationSchema');
    return await validateSKUUniqueness(sku, organizationId);
  } catch (error) {
    logWarn('SKU uniqueness check failed', {
      component: 'product.ts',
      action: 'checkSKUUniqueness',
      metadata: { sku, organizationId, error },
    });
    return true; // Allow if check fails to avoid blocking user
  }
}

// ========== LEGACY HELPER FUNCTIONS (BACKWARD COMPATIBILITY) ==========

/**
 * Type definition for product form values
 */
export type ProductFormValues = z.infer<typeof productEditSchema>;

/**
 * Helper to convert ProductAttribute[] to Record<string, string>
 * @param attributes - Array of product attributes
 * @returns Record with attribute names as keys and values as values
 */
const attributesToRecord = (
  attributes: ProductAttribute[] | Record<string, string> | undefined
): Record<string, string> => {
  if (!attributes) return {};

  if (Array.isArray(attributes)) {
    // Convert attribute array to record
    return attributes.reduce(
      (acc, attr) => {
        if (attr.name && attr.value) {
          acc[attr.name] = attr.value;
        }
        return acc;
      },
      {} as Record<string, string>
    );
  }

  // Already a record
  return attributes;
};

/**
 * Extract price from a product
 * @param product - Product object
 * @returns Price value or default
 */
const extractPrice = (product?: Product): number => {
  if (!product) return 0.01;

  // Check if there's a prices array with values
  if (product.prices && product.prices.length > 0) {
    // Return the first price's amount or fallback to default
    return product.prices[0].amount || 0.01;
  }

  // Fallback to default value
  return 0.01;
};

/**
 * Extract category label and value from various category formats
 * @param category - Category data in different possible formats
 * @returns Object with label and value properties for the form
 */
export const extractCategoryInfo = (
  category: ProductCategory[] | ProductCategory | string
): { label: string; value: string | number } => {
  // Handle string category
  if (typeof category === 'string') {
    return { label: category, value: category };
  }

  // Handle array of categories (take the last one, which is the leaf)
  if (Array.isArray(category) && category.length > 0) {
    const leafCategory = category[category.length - 1];
    return {
      label: leafCategory.name || '',
      value: leafCategory.id || '',
    };
  }

  // Handle single category object
  if (category && typeof category === 'object' && !Array.isArray(category)) {
    const cat = category as ProductCategory;
    return {
      label: cat.name ?? '',
      value: cat.id ?? '',
    };
  }

  // Default fallback if none of the above
  return { label: '', value: '' };
};

/**
 * Extract family value from product
 * @param product - Product object to extract family from
 * @returns Family ID or null
 */
const extractFamily = (product?: Product): number | null => {
  if (!product?.family) return null;

  // Handle different family field formats
  if (typeof product.family === 'number') return product.family;
  if (typeof product.family === 'object' && 'id' in product.family) {
    return product.family.id;
  }

  return null;
};

/**
 * Returns default values for the product form
 * @param initialProduct - Optional product data to initialize with
 * @returns Default values for the product form
 */
export const getDefaultProductValues = (initialProduct?: Product): ProductFormValues => {
  return {
    name: initialProduct?.name ?? '',
    description: initialProduct?.description ?? '',
    sku: initialProduct?.sku ?? '',
    price: extractPrice(initialProduct),
    category: initialProduct?.category
      ? extractCategoryInfo(initialProduct.category).value
      : undefined,
    is_active: initialProduct?.is_active ?? true,
    brand: initialProduct?.brand ?? '',
    barcode: initialProduct?.barcode ?? '',
    family: extractFamily(initialProduct),
    attributes: attributesToRecord(initialProduct?.attributes),
  };
};

/**
 * Usage example:
 *
 * import { useForm } from 'react-hook-form'
 * import { zodResolver } from '@hookform/resolvers/zod'
 * import { productEditSchema, getDefaultProductValues, ProductFormValues } from '@/schemas/product'
 *
 * // Inside your component:
 * const form = useForm<ProductFormValues>({
 *   resolver: zodResolver(productEditSchema),
 *   defaultValues: getDefaultProductValues(initialProduct)
 * })
 */

/**
 * Enhanced SKU validation with detailed rules
 */
export function validateSKUEnhanced(sku: string): {
  isValid: boolean;
  error?: string;
} {
  if (!sku || sku.trim().length === 0) {
    return { isValid: false, error: 'SKU is required' };
  }

  // Length check
  if (sku.length < 3) {
    return { isValid: false, error: 'SKU must be at least 3 characters long' };
  }

  if (sku.length > 50) {
    return { isValid: false, error: 'SKU must not exceed 50 characters' };
  }

  // Character validation - only alphanumeric, hyphens, and underscores
  if (!/^[A-Za-z0-9_-]+$/.test(sku)) {
    return {
      isValid: false,
      error: 'SKU can only contain letters, numbers, hyphens, and underscores',
    };
  }

  // Cannot start or end with hyphen
  if (sku.startsWith('-') || sku.endsWith('-')) {
    return { isValid: false, error: 'SKU cannot start or end with a hyphen' };
  }

  // Check for control characters using Unicode categories instead of hex ranges
  if (/\p{C}/u.test(sku)) {
    return { isValid: false, error: 'SKU contains invalid control characters' };
  }

  return { isValid: true };
}

/**
 * Enhanced product name validation
 */
export function validateNameEnhanced(name: string): {
  isValid: boolean;
  error?: string;
} {
  if (!name || name.trim().length === 0) {
    return { isValid: false, error: 'Product name is required' };
  }

  if (name.trim().length < 2) {
    return { isValid: false, error: 'Product name must be at least 2 characters long' };
  }

  if (name.length > 200) {
    return { isValid: false, error: 'Product name must not exceed 200 characters' };
  }

  // Check for control characters using Unicode categories
  if (/\p{C}/u.test(name)) {
    return { isValid: false, error: 'Product name contains invalid characters' };
  }

  return { isValid: true };
}

/**
 * Enhanced brand validation
 */
export function validateBrandEnhanced(brand: string): {
  isValid: boolean;
  error?: string;
} {
  // Brand is optional
  if (!brand || brand.trim().length === 0) {
    return { isValid: true };
  }

  if (brand.length > 100) {
    return { isValid: false, error: 'Brand must not exceed 100 characters' };
  }

  // Check for control characters using Unicode categories
  if (/\p{C}/u.test(brand)) {
    return { isValid: false, error: 'Brand contains invalid characters' };
  }

  return { isValid: true };
}

/**
 * Enhanced GTIN validation - More lenient for PIM usage
 * Note: Error messages should be handled at the component level for proper translation
 */
export function validateGTINEnhanced(gtin: string): {
  isValid: boolean;
  error?: string;
} {
  // GTIN is optional
  if (!gtin || gtin.trim().length === 0) {
    return { isValid: true };
  }

  if (!isValidBarcodeFormat(gtin)) {
    return {
      isValid: false,
      error: 'GTIN_FORMAT_ERROR', // Translation key to be handled by components
    };
  }

  return { isValid: true };
}

/**
 * Enhanced description validation
 */
export function validateDescriptionEnhanced(description: string): {
  isValid: boolean;
  error?: string;
} {
  // Description is optional
  if (!description || description.trim().length === 0) {
    return { isValid: true };
  }

  if (description.length > 2000) {
    return { isValid: false, error: 'Description must not exceed 2000 characters' };
  }

  // Check for problematic control characters (excluding allowed whitespace like \t, \n)
  if (/\p{Cc}/u.test(description.replace(/[\t\n\r]/g, ''))) {
    return { isValid: false, error: 'Description contains invalid control characters' };
  }

  return { isValid: true };
}

// Simple field validation dispatcher
export function validateFieldSimple(
  fieldName: string,
  value: string
): { isValid: boolean; error?: string } {
  switch (fieldName) {
    case 'sku':
      return validateSKUEnhanced(value);
    case 'name':
      return validateNameEnhanced(value);
    case 'brand':
      return validateBrandEnhanced(value);
    case 'barcode':
    case 'gtin':
      return validateGTINEnhanced(value);
    case 'description':
      return validateDescriptionEnhanced(value);
    default:
      return { isValid: true }; // No validation for other fields yet
  }
}
